import React, { FC } from 'react'
import { get, mapValues } from 'lodash'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { Hidden, Dialog, DialogContent } from '@mui/material'

import { IconType } from '../../types/interface/icon.interface'
import { VEHICLE_COVERAGE_TYPE_TRANSLATION_KEYS } from '../../constants'

import CoverageInfoList, { LocalizedListItemType } from './CoverageInfoList'
import { filterTranslationByCondition } from '../../service/util.service'
import { Typography, BrandIconShared } from '@dg-shared'
import { useTranslate } from '../../hooks/shared'
import { FSMStateType, ServiceSend } from '../../types/interface/fsm.interface'
import { StateValue } from 'xstate'

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
      display: 'flex',
      justifyContent: 'center',
    },
  },

  title: {
    marginBottom: theme.spacing(1),
  },

  coverText: {
    marginBottom: theme.spacing(1),
  },

  closeBtn: {
    padding: 0,
    height: '2rem',
    color: theme.palette.primary.main,
  },

  dialogContentStyle: {
    padding: 16,
  },

  closeIcon: {
    color: theme.brand.colors.headingMain,
    position: 'absolute',
    top: 10,
    right: 10,
    cursor: 'pointer',
    fontWeight: 100,
  },

  disclaimer: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      textAlign: 'start',
    },
    '& a': {
      textDecoration: 'underline',
      color: 'inherit',
    },
    '& button': {
      color: 'inherit',
    },
  },

  link: {
    ...theme.brand.assets.layout?.buttonLink,
  },
}))

interface CoverageDetailedInfoProps {
  coverageType: string | number
  onClose?: () => void
  showDialogMobile?: boolean
  translationCondition?: string
  send?: ServiceSend
  state?: StateValue | FSMStateType
}

const CoverageDetailedInfo: FC<CoverageDetailedInfoProps> = ({
  coverageType,
  translationCondition,
  showDialogMobile = true,
  onClose,
}) => {
  const { classes } = useStyles()
  const { t } = useTranslate()

  const coverageTranslationKeys = get(
    VEHICLE_COVERAGE_TYPE_TRANSLATION_KEYS,
    [PARTNER_NAME, coverageType],
    null
  )

  const {
    name,
    description,
    list,
    additionalInfo = null,
  } = mapValues(coverageTranslationKeys, (el) => t(el))

  const infoList = (
    <CoverageInfoList
      list={filterTranslationByCondition(
        list as Array<LocalizedListItemType>,
        translationCondition
      )}
    />
  )

  return (
    <>
      <Hidden mdDown={showDialogMobile}>
        <section id='review-coverage-wrapper' className={classes.container}>
          <section className={classes.header}>
            {name && (
              <Typography testId='review-coverage-title' type='h2' className={classes.title}>
                {name}
              </Typography>
            )}
          </section>
          <Typography
            testId='review-coverage-description'
            className={classes.coverText}
            color='textMain'
          >
            {description}
          </Typography>
          {infoList}
          <Typography>{additionalInfo}</Typography>
        </section>
      </Hidden>

      <Hidden mdUp mdDown={!showDialogMobile}>
        <Dialog onClose={() => onClose()} open={true} maxWidth='xs'>
          <DialogContent className={classes.dialogContentStyle}>
            <BrandIconShared
              type={IconType.ERROR}
              customClass={classes.closeIcon}
              onClick={() => {
                onClose()
              }}
              action
            />
            <Typography type='h2'>{name}</Typography>
            <Typography>{description}</Typography>
            {infoList}
            <Typography>{additionalInfo}</Typography>
          </DialogContent>
        </Dialog>
      </Hidden>
    </>
  )
}

export default CoverageDetailedInfo
